import { Component, ElementRef, HostListener, inject, Renderer2, effect, DestroyRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, switchMap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { UnirCoreService, SpaConfigEnvironment } from '@unir/core';
import { UnirCrosscuttingService } from '@unir/core/auth/crosscutting';
import { appRoutesType, MAINTENANCE_MODE } from './app.routes';
import { ParamsService } from './services/params.service';
import { MobileService } from './services/mobile.service';
import { DarkThemeService } from './services/dark-theme.service';
import { MessagesService } from './services/messages.service';
import { MenuService } from './services/menu.service';
import { MenuActionType } from './models/app/menu-action.model';
import { UNIVERSITIES } from './shared/consts/universities.const';
import { MainMenuComponent } from './components/menu/main-menu/main-menu.component';

type WindowDataLayer = Window & {
	dataLayer?: unknown[];
};

@Component({
	standalone: true,
	imports: [CommonModule, RouterModule, ToastModule, MainMenuComponent],
	selector: 'unir-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	providers: [MessageService]
})
export class AppComponent {
	@HostListener('window:resize')
	onResize() {
		this.mobileService.mobileView.set(window.innerWidth <= 1024);
	}

	readonly #messageService = inject(MessagesService);
	readonly #primengMessagesService = inject(MessageService);

	protected coreService = inject(UnirCoreService);
	protected crosscuttingService = inject(UnirCrosscuttingService);
	protected menuService = inject(MenuService);

	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private paramsService = inject(ParamsService);
	private renderer = inject(Renderer2);
	private el = inject(ElementRef);
	private mobileService = inject(MobileService);
	private darkThemeService = inject(DarkThemeService);
	private destroyRef = inject(DestroyRef);

	protected showMenu = signal(false);

	private idSubject = signal<number | undefined>(undefined);

	private readyEffect = effect(
		() => {
			const subjectId = this.idSubject();
			if (subjectId && this.coreService.configurationLoaded() && this.crosscuttingService.user()) {
				this.paramsService.getTopicsBySubject(subjectId);
			}
		},
		{ allowSignalWrites: true }
	);

	private topicsEffect = effect(
		() => {
			const topicsBySubject = this.paramsService.topicsBySubject();
			if (topicsBySubject) {
				this.initializeGTM();
				this.darkThemeService.checkThemeMode();
				this.manageTabbing();
				this.onResize();
			}
		},
		{ allowSignalWrites: true }
	);

	private topicsNotFoundEffect = effect(() => {
		if (this.paramsService.topicsBySubject() === null) {
			this.checkNotFound();
		}
	});

	private mobileEffect = effect(
		() => {
			if (this.showMenu()) {
				this.menuService.mainMenuClosed.set(this.mobileService.mobileView());
			}
		},
		{ allowSignalWrites: true }
	);

	constructor() {
		this.listenToRouterEvents();
		this.messageService();
	}

	closeMainMenu() {
		this.menuService.mainMenuClosed.set(true);
	}

	private listenToRouterEvents() {
		this.router.events
			.pipe(
				filter((event): event is NavigationEnd => event instanceof NavigationEnd),
				map(() => this.route.root.firstChild),
				filter((firstChild): firstChild is NonNullable<typeof firstChild> => !!firstChild),
				switchMap((firstChild) => firstChild.params),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((params) => {
				if (MAINTENANCE_MODE) {
					this.showMenu.set(false);
					return;
				}
				const subjectIdParam = params['subjectId'];
				if (subjectIdParam) {
					if (!/^\d+$/.test(subjectIdParam)) {
						this.idSubject.set(undefined);
					} else {
						const id = parseInt(subjectIdParam, 10);
						if (isNaN(id)) {
							this.idSubject.set(undefined);
						} else {
							this.idSubject.set(id);
						}
					}
				} else {
					this.idSubject.set(undefined);
				}
				if (this.idSubject() === undefined) {
					this.showMenu.set(false);
					this.checkNotFound();
				}
				this.checkMenu();
			});
	}

	private checkNotFound() {
		const url = this.router.url;
		const allowedRoutes = [appRoutesType.CROSSCUTTING, appRoutesType.LTI, appRoutesType.NOT_FOUND];
		const isAllowedRoute = allowedRoutes.some((route) => url.includes(route));

		if (!isAllowedRoute) {
			this.router.navigate([appRoutesType.NOT_FOUND]);
		}
	}

	private checkMenu() {
		const urlSegments = this.router.url.split('/').filter((s) => s);
		if (urlSegments.length === 0) {
			this.showMenu.set(false);
			return;
		}
		const validRoutes = [appRoutesType.CHAT, appRoutesType.TRAINING];
		const isMenuPage =
			(urlSegments.length === 1 && /^\d+$/.test(urlSegments[0])) ||
			(urlSegments.length > 1 && validRoutes.includes(urlSegments[1] as appRoutesType));
		this.showMenu.set(isMenuPage);
	}

	private messageService() {
		this.#messageService.message$.pipe(takeUntilDestroyed(), filter(Boolean)).subscribe((message) => {
			this.#primengMessagesService.clear();
			this.#primengMessagesService.add({
				key: 'message-toast',
				severity: message.type,
				summary: message.title,
				detail: message.text
			});
			if (message.redirect) {
				this.menuService.editAction({ action: MenuActionType.GO_HOME });
			}
		});
	}

	private manageTabbing() {
		this.renderer.listen('document', 'keydown', (event) => {
			if (event.key === 'Tab') {
				this.renderer.addClass(this.el.nativeElement.ownerDocument.body, 'user-is-tabbing');
			}
		});

		this.renderer.listen('document', 'mousedown', () => {
			this.renderer.removeClass(this.el.nativeElement.ownerDocument.body, 'user-is-tabbing');
		});
	}

	private initializeGTM() {
		const domain = window.location.host;
		if (
			this.coreService.spaConfig()?.environment === SpaConfigEnvironment.PRO &&
			domain.includes(UNIVERSITIES.UNIR_ES.host)
		) {
			(window as WindowDataLayer).dataLayer = (window as WindowDataLayer).dataLayer || [];

			const gtmScript = this.renderer.createElement('script');
			gtmScript.innerHTML = `
				(function (w, d, s, l, i) {
					w[l] = w[l] || [];
					w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
					var f = d.getElementsByTagName(s)[0],
						j = d.createElement(s),
						dl = l != 'dataLayer' ? '&l=' + l : '';
					j.async = true;
					j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
					f.parentNode.insertBefore(j, f);
				})(window, document, 'script', 'dataLayer', 'GTM-5DWZ5KQB');
			`;
			this.renderer.insertBefore(document.head, gtmScript, document.head.firstChild);

			const noscript = this.renderer.createElement('noscript');
			const iframe = this.renderer.createElement('iframe');
			this.renderer.setAttribute(iframe, 'src', 'https://www.googletagmanager.com/ns.html?id=GTM-5DWZ5KQB');
			this.renderer.setAttribute(iframe, 'height', '0');
			this.renderer.setAttribute(iframe, 'width', '0');
			this.renderer.setStyle(iframe, 'display', 'none');
			this.renderer.setStyle(iframe, 'visibility', 'hidden');
			this.renderer.appendChild(noscript, iframe);
			this.renderer.insertBefore(document.body, noscript, document.body.firstChild);

			(window as WindowDataLayer).dataLayer?.push({
				event: 'student_context',
				student_code: this.crosscuttingService.user()?.info?.alumno_id,
				subject_code: (this.idSubject() ?? 0).toString()
			});
		}
	}
}
